import { useStore } from '@nanostores/react';
import { Navigate, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import { $isAuthenticated, $authLoading } from '~/lib/stores/auth';
import { $showAuthModal } from '~/lib/stores/authModal';
import { markDesktopSignup } from '~/lib/attribution';
import SignInHold from './SignInHold';
import LoadingScreen from './LoadingScreen';
import type { ReactNode } from 'react';

interface ProtectedRouteProps {
  children: ReactNode;
  /**
   * Sign in on top of this page instead of leaving it. For handoff pages whose
   * URL carries what the other app needs (see isStayOnPagePath): once signed
   * in, the page renders with its query intact.
   */
  stayOnPage?: boolean;
}

export default function ProtectedRoute({ children, stayOnPage = false }: ProtectedRouteProps) {
  const isAuthenticated = useStore($isAuthenticated);
  const authLoading = useStore($authLoading);
  const location = useLocation();

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      // signing up from the desktop app's handoff page: tell the signup which platform
      if (location.pathname.startsWith('/desktop-auth')) {
        markDesktopSignup();
      }

      $showAuthModal.set(true);
    }
  }, [authLoading, isAuthenticated, location.pathname]);

  if (authLoading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated && stayOnPage) {
    return <SignInHold />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/" state={{ from: location.pathname }} replace />;
  }

  return <>{children}</>;
}
